import type { RolodexV3UserScope } from 'features/permissions/utils/policyScopeEvaluation'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import type { RoleWithPermission } from 'types'

const ROOT_SCOPE_TYPE = 'root'

/** Parses the comma-separated deploy-time list. An unexpanded `$NAME` placeholder counts as empty. */
export const parseDisabledIds = (
  raw: string | undefined
): ReadonlySet<string> =>
  new Set(
    (raw ?? '')
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id && !id.startsWith('$'))
  )

const unquote = (value: string): string => value.replace(/^"|"$/g, '')

const safeDecode = (segment: string): string => {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

/** True when a URL names a listed org or site, in the `orgId`/`siteId` search params or as a path segment. */
export const urlNamesDisabledId = (
  url: string,
  disabledIds: ReadonlySet<string>
): boolean => {
  if (disabledIds.size === 0) return false
  const { pathname, searchParams } = new URL(url, 'https://forecast.invalid')
  const searchIds = [SEARCH_PARAM_ORG_ID, SEARCH_PARAM_SITE_ID].flatMap((key) =>
    searchParams.getAll(key).map(unquote)
  )
  const pathIds = pathname.split('/').map(safeDecode)
  return [...searchIds, ...pathIds].some((id) => disabledIds.has(id))
}

type RoleOrg = Pick<RoleWithPermission, 'organization_id' | 'organizationId'>

/**
 * True when every org the user belongs to is listed, or, for a user with no org,
 * every site. Unit and other scopes are ignored, so listing an org covers its
 * sites and units. Users with root access or no known org or site are never in
 * this group.
 */
export const userIsOnlyInDisabledIds = (
  roles: readonly RoleOrg[],
  scopes: readonly RolodexV3UserScope[] | undefined,
  disabledIds: ReadonlySet<string>
): boolean => {
  if (disabledIds.size === 0) return false
  if (scopes?.some((scope) => scope.type === ROOT_SCOPE_TYPE)) return false
  const scopeNames = (type: string) =>
    (scopes ?? [])
      .filter((scope) => scope.type === type)
      .map(({ name }) => name)
  const orgs = [
    ...roles.flatMap(
      (role) => role.organization_id ?? role.organizationId ?? []
    ),
    ...scopeNames('organization'),
  ]
  const memberships = orgs.length > 0 ? orgs : scopeNames('site')
  return (
    memberships.length > 0 && memberships.every((id) => disabledIds.has(id))
  )
}

/** True when Mixpanel stays off for the signed-in user for the whole session. Fails closed while a set list waits on scopes. */
export const isUserGateClosed = ({
  isInternalMember,
  roles,
  scopes,
  scopesError,
  disabledIds,
}: {
  isInternalMember: boolean
  roles: readonly RoleOrg[]
  scopes: readonly RolodexV3UserScope[] | undefined
  scopesError: unknown
  disabledIds: ReadonlySet<string>
}): boolean => {
  if (isInternalMember || disabledIds.size === 0) return false
  if (!scopes && !scopesError) return true
  return userIsOnlyInDisabledIds(roles, scopes, disabledIds)
}

type MixpanelEventPayload = { properties?: { $current_url?: unknown } }

/**
 * Drops outgoing Mixpanel data while the gate is closed, and events recorded on
 * a listed URL that flush after the user leaves it. Mixpanel sends the pageview
 * synchronously on `pushState`, before any app code can opt out. Mixpanel rejects
 * hooks whose source doesn't start with `function`.
 */
export const createMixpanelGateHooks = (
  isGateClosed: () => boolean,
  disabledIds: ReadonlySet<string>
) => ({
  before_send_events: function <T extends MixpanelEventPayload>(
    data: T
  ): T | null {
    const eventUrl = data.properties?.$current_url
    const recordedOnListedUrl =
      typeof eventUrl === 'string' && urlNamesDisabledId(eventUrl, disabledIds)
    return isGateClosed() || recordedOnListedUrl ? null : data
  },
  before_send_people: function <T>(data: T): T | null {
    return isGateClosed() ? null : data
  },
})
